import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../../redux/actions';
import useMarketingLiveRefresh from './useMarketingLiveRefresh';

export function useOpportunityDetail(id) {
  const dispatch = useDispatch();

  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  // เก็บ id ล่าสุดที่โหลดสำเร็จแล้ว — refetch หลังบันทึก/แก้ไข (เปลี่ยนสถานะ, แนบเอกสาร
  // ฯลฯ) จะไม่โชว์ full-page spinner ซ้ำ เพราะเป็น id เดิม โชว์เฉพาะตอนเข้าหน้าครั้งแรก
  // หรือสลับไป id อื่นจริง ๆ เท่านั้น
  const loadedIdRef = useRef(null);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const closeSnack = () => setSnack((p) => ({ ...p, open: false }));

  const fetchDetail = useCallback(async () => {
    if (loadedIdRef.current !== id) setLoading(true);
    try {
      const data = await dispatch(actions.getOpportunity({ id }));
      setOpportunity(data);
      loadedIdRef.current = id;
    } catch (err) {
      showSnack(err.message || 'โหลดข้อมูลไม่สำเร็จ', 'error');
    } finally {
      setLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // เห็นการแก้จากที่อื่นเอง — เงียบ (ไม่ snack error) เพราะโอกาสอาจถูกลบระหว่างเปิด popup อยู่ ปล่อยข้อมูลเดิมไว้
  useMarketingLiveRefresh(async () => {
    const data = await dispatch(actions.getOpportunity({ id, silent: true }));
    setOpportunity((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
  }, { enabled: loadedIdRef.current === id });

  // อัพเดตค่าที่เห็นทันทีหลังแก้ไข (ไม่ต้องรอ round-trip ของ refetch) — เหมือน
  // onLocalUpdate ของ ProjectSingleDashboard.js (ดู CLAUDE.md) ใช้คู่กับ refetch เสมอ
  // (เรียก refetch ต่อจาก patch สำเร็จ) ให้ค่าจริงจาก server ชนะทับในที่สุดถ้าไม่ตรงกัน
  const applyLocalPatch = (patch) => setOpportunity((prev) => (prev ? { ...prev, ...patch } : prev));

  return {
    opportunity, loading, snack, showSnack, closeSnack, refetch: fetchDetail, applyLocalPatch,
  };
}

export default useOpportunityDetail;
